import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '@/lib/api'

interface Field {
  key: string
  label: string
  type: 'text' | 'email' | 'tel' | 'textarea' | 'checkbox' | 'select'
  required?: boolean
  placeholder?: string
  options?: string[]
}

interface FormPublic {
  id: string
  name: string
  publicSlug: string
  fieldsJson: Field[]
  consentRequired: boolean
  consentText: string | null
}

function captureUtms() {
  const url = new URL(window.location.href)
  const sp = url.searchParams
  return {
    utm_source: sp.get('utm_source') ?? undefined,
    utm_medium: sp.get('utm_medium') ?? undefined,
    utm_campaign: sp.get('utm_campaign') ?? undefined,
    utm_term: sp.get('utm_term') ?? undefined,
    utm_content: sp.get('utm_content') ?? undefined,
    gclid: sp.get('gclid') ?? undefined,
    fbclid: sp.get('fbclid') ?? undefined,
  }
}

export function FormViewerPage() {
  const { slug } = useParams<{ slug: string }>()
  const [form, setForm] = useState<FormPublic | null>(null)
  const [data, setData] = useState<Record<string, string>>({})
  const [consent, setConsent] = useState({ email: false, sms: false, whatsapp: false })
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    if (!slug) return
    api<FormPublic>(`/p/forms/${slug}`)
      .then(setForm)
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
  }, [slug])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!slug || !form) return
    setBusy(true)
    setErr(null)
    try {
      const r = await api<{ message: string }>(`/p/forms/${slug}`, {
        method: 'POST',
        body: JSON.stringify({
          data,
          consent,
          utms: captureUtms(),
          referrer: document.referrer || undefined,
          landingUrl: window.location.href,
        }),
      })
      setDone(r.message)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  if (err && !form) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-6">
        <div className="bp-card p-6 max-w-md w-full text-sm font-mono text-red-700">{err}</div>
      </div>
    )
  }
  if (!form) {
    return (
      <div className="min-h-screen bg-surface flex items-center justify-center p-6">
        <div className="text-xs font-mono text-ink-700/50">Cargando…</div>
      </div>
    )
  }
  if (done) {
    return (
      <div className="min-h-screen bg-surface bp-grid flex items-center justify-center p-6">
        <div className="bp-card p-8 max-w-md w-full text-center">
          <div className="text-blueprint-700 text-3xl mb-2">✓</div>
          <h1 className="text-lg font-medium">{done}</h1>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-surface bp-grid flex items-center justify-center p-6">
      <form
        onSubmit={(e) => void submit(e)}
        className="bp-card p-6 max-w-lg w-full space-y-3"
      >
        <header className="mb-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-blueprint-700">
            JR2 · {form.publicSlug}
          </div>
          <h1 className="text-xl font-medium">{form.name}</h1>
        </header>

        {form.fieldsJson.map((f) => (
          <label key={f.key} className="block">
            <span className="text-xs text-ink-700">
              {f.label}
              {f.required && <span className="text-red-600 ml-0.5">*</span>}
            </span>
            {f.type === 'textarea' ? (
              <textarea
                value={data[f.key] ?? ''}
                onChange={(e) => setData((d) => ({ ...d, [f.key]: e.target.value }))}
                required={f.required}
                placeholder={f.placeholder}
                className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm min-h-[100px]"
              />
            ) : f.type === 'select' ? (
              <select
                value={data[f.key] ?? ''}
                onChange={(e) => setData((d) => ({ ...d, [f.key]: e.target.value }))}
                required={f.required}
                className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
              >
                <option value="">—</option>
                {(f.options ?? []).map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={f.type}
                value={data[f.key] ?? ''}
                onChange={(e) => setData((d) => ({ ...d, [f.key]: e.target.value }))}
                required={f.required}
                placeholder={f.placeholder}
                className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm"
              />
            )}
          </label>
        ))}

        {form.consentRequired && (
          <fieldset className="border border-ink-900/10 rounded p-3 text-xs text-ink-700 space-y-1.5">
            <legend className="px-1 text-[10px] font-mono uppercase tracking-wider text-ink-700/60">
              consentimientos (LSSI / RGPD)
            </legend>
            {form.consentText && <p className="text-[11px] leading-relaxed">{form.consentText}</p>}
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={consent.email}
                onChange={(e) => setConsent((c) => ({ ...c, email: e.target.checked }))}
                className="mt-0.5"
              />
              <span>Acepto recibir comunicaciones por email.</span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={consent.whatsapp}
                onChange={(e) => setConsent((c) => ({ ...c, whatsapp: e.target.checked }))}
                className="mt-0.5"
              />
              <span>Acepto recibir comunicaciones por WhatsApp.</span>
            </label>
            <label className="flex items-start gap-2">
              <input
                type="checkbox"
                checked={consent.sms}
                onChange={(e) => setConsent((c) => ({ ...c, sms: e.target.checked }))}
                className="mt-0.5"
              />
              <span>Acepto recibir comunicaciones por SMS.</span>
            </label>
          </fieldset>
        )}

        {err && (
          <div className="text-xs font-mono text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
            {err}
          </div>
        )}

        <button type="submit" className="bp-btn-primary w-full justify-center" disabled={busy}>
          {busy ? 'Enviando…' : 'Enviar'}
        </button>
      </form>
    </div>
  )
}
